import { forwardRef, type HTMLAttributes } from "react";
import { Panel } from "@pilot-protocol/theme/chrome";
import { cn } from "./lib/cn.js";

/**
 * Card — chrome-Panel-based surface for primary content containers.
 *
 * Renders as a 2-layer chamfered Panel from `@pilot-protocol/theme/chrome`
 * so cards inherit the scifi vocabulary (hairline outline, chamfered
 * corners, mono-leaning typography via the chrome token defaults).
 *
 * Variants:
 *   - `hairline` (default) — translucent dark interior, hairline outline.
 *     Reads as a recessed window over the page background.
 *   - `solid` — opaque `--pp-bg-strong` interior. Use for modal-like
 *     surfaces, tooltips, or anywhere card content needs full contrast
 *     independent of the page behind it.
 *
 * Spacing: subcomponents (`CardHeader`, `CardContent`, `CardFooter`)
 * own their own padding. The Panel itself doesn't pad — that's why the
 * subcomponents use `p-6` (24px) class for breathing room.
 */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual treatment. Default `hairline`. */
  variant?: "hairline" | "solid";
  /** Optional accent stripe color (mirrors Panel.accent). */
  accent?: string;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = "hairline", accent, ...props }, ref) => (
    <Panel
      ref={ref}
      variant={variant}
      accent={accent}
      className={cn("pp-card text-[var(--pp-text)]", className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        "font-[var(--pp-font-family)] font-semibold leading-none tracking-[var(--pp-tracking-caps)] uppercase",
        "text-[var(--pp-font-lg)]",
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      "text-[var(--pp-font-sm)] text-[var(--pp-text-muted)] tracking-[var(--pp-tracking-caps)] uppercase",
      className,
    )}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";
